/**
 * The icons the legacy public pages use, inlined.
 *
 * Legacy imports these from `lucide-react` (pinned `^0.545.0`). This module
 * reproduced them locally so that the parity migration introduced no
 * dependency, no `package.json` change, and no lockfile change: dependency
 * decisions belonged to their own slice, after parity was complete.
 *
 * **That decision has now been taken (D1): `lucide-react` is the icon source.**
 * `/why` imports from the library directly and no longer imports from here.
 * This module remains only for `Landing`, `Bottleneck`, `WorkFit`, and
 * `Perspective`, which are out of scope for the slice that added the
 * dependency. It is a shrinking remnant, not a system to extend — every icon
 * below has an exact `lucide-react` equivalent, so add nothing here. The
 * remaining four pages should move to the library in their own slice, after
 * which this file is deleted.
 *
 * **The path data below is not hand-drawn.** It was extracted verbatim from
 * `lucide-react@0.545.0` in `node_modules` and matches that version's
 * `__iconNode` definitions exactly, so the rendered geometry is identical.
 * Two of them resolve through deprecated aliases and would have been wrong if
 * guessed: `CheckCircle` re-exports `circle-check-big`, and `BarChart2`
 * re-exports `chart-no-axes-column`. Both are reproduced from the target of the
 * alias, not from the older icon that once carried the name.
 *
 * The shared attributes match lucide's defaults. Lucide also adds
 * `lucide lucide-<name>` classes, which carry no styling of their own and are
 * omitted; every visual property comes from the attributes below plus the
 * caller's `className`, which carries the size exactly as legacy sets it
 * (`w-4 h-4`, `w-5 h-5`, `w-3.5 h-3.5`).
 *
 * The `ExternalLinkIcon` that lived here was `/why`'s only consumer and went
 * with it to `lucide-react`. Its call sites still use that name, aliased at the
 * import, because the link focus-ring scanner in test/visualSystem.test.ts
 * treats any JSX tag ending in `Link` as a link element and asserts the ring on
 * its call sites. An icon is not a link. Same reason `SkipToContent` is not
 * `SkipLink`.
 */
type IconProps = { className?: string };

const BASE = {
  xmlns: 'http://www.w3.org/2000/svg',
  width: 24,
  height: 24,
  viewBox: '0 0 24 24',
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 2,
  strokeLinecap: 'round',
  strokeLinejoin: 'round',
} as const;

export function ArrowRight({ className }: IconProps) {
  return (
    <svg {...BASE} className={className} aria-hidden="true" focusable="false">
      <path d="M5 12h14" />
      <path d="m12 5 7 7-7 7" />
    </svg>
  );
}

export function ChevronDown({ className }: IconProps) {
  return (
    <svg {...BASE} className={className} aria-hidden="true" focusable="false">
      <path d="m6 9 6 6 6-6" />
    </svg>
  );
}

export function ChevronRight({ className }: IconProps) {
  return (
    <svg {...BASE} className={className} aria-hidden="true" focusable="false">
      <path d="m9 18 6-6-6-6" />
    </svg>
  );
}

/** lucide `CheckCircle` re-exports `circle-check-big`. */
export function CheckCircle({ className }: IconProps) {
  return (
    <svg {...BASE} className={className} aria-hidden="true" focusable="false">
      <path d="M21.801 10A10 10 0 1 1 17 3.335" />
      <path d="m9 11 3 3L22 4" />
    </svg>
  );
}

export function Zap({ className }: IconProps) {
  return (
    <svg {...BASE} className={className} aria-hidden="true" focusable="false">
      <path d="M4 14a1 1 0 0 1-.78-1.63l9.9-10.2a.5.5 0 0 1 .86.46l-1.92 6.02A1 1 0 0 0 13 10h7a1 1 0 0 1 .78 1.63l-9.9 10.2a.5.5 0 0 1-.86-.46l1.92-6.02A1 1 0 0 0 11 14z" />
    </svg>
  );
}

/** lucide `BarChart2` re-exports `chart-no-axes-column`. */
export function BarChart2({ className }: IconProps) {
  return (
    <svg {...BASE} className={className} aria-hidden="true" focusable="false">
      <path d="M5 21v-6" />
      <path d="M12 21V3" />
      <path d="M19 21V9" />
    </svg>
  );
}

export function FileText({ className }: IconProps) {
  return (
    <svg {...BASE} className={className} aria-hidden="true" focusable="false">
      <path d="M15 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7Z" />
      <path d="M14 2v4a2 2 0 0 0 2 2h4" />
      <path d="M10 9H8" />
      <path d="M16 13H8" />
      <path d="M16 17H8" />
    </svg>
  );
}

export function Calendar({ className }: IconProps) {
  return (
    <svg {...BASE} className={className} aria-hidden="true" focusable="false">
      <path d="M8 2v4" />
      <path d="M16 2v4" />
      <rect width="18" height="18" x="3" y="4" rx="2" />
      <path d="M3 10h18" />
    </svg>
  );
}
